import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Flame, ClipboardList, Boxes, Timer, ShieldCheck, LineChart, Store, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FEATURES = [
  { icon: Flame, label: "Kitchen Operations" },
  { icon: ClipboardList, label: "Order Management" },
  { icon: Boxes, label: "Inventory & Ingredients" },
  { icon: Timer, label: "Production Workflow" },
  { icon: ShieldCheck, label: "Quality Control" },
  { icon: LineChart, label: "Analytics & Reporting" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/dashboard"); // HomeRoute resolves the correct dashboard/portal by role
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-background factory-grid brand-glow">
      {/* Visual / branding panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Dish Factory commercial kitchen operations" className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background via-background/75 to-background/35" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img src="/df-logo-full.png" alt="DishFactory" className="h-9 w-auto self-start" />

          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 mb-5">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-widest text-amber-300">Cloud Kitchen OS</span>
            </div>
            <h2 className="text-4xl font-display font-extrabold text-white max-w-lg leading-[1.1]">
              The operating system behind your cloud kitchen.
            </h2>
            <p className="text-muted-foreground mt-4 max-w-md">
              One kitchen. Many brands. Every gram traced from purchase to plate — with confidential
              client-facing billing built in.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-8 max-w-lg">
              {FEATURES.map((f) => (
                <div key={f.label} className="flex items-center gap-3 rounded-xl border border-border bg-card/60 backdrop-blur-sm px-3.5 py-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center shrink-0">
                    <f.icon className="h-[18px] w-[18px] text-amber-300" />
                  </div>
                  <span className="text-sm font-medium text-slate-200">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Store className="h-4 w-4 text-amber-300" />
            <span>Multiple food brands managed from a single production floor.</span>
          </div>
        </div>
      </div>

      {/* Login form panel */}
      <div className="w-full lg:w-[460px] flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-10 justify-center">
            <img src="/df-logo-header.png" alt="DishFactory" className="h-8 w-auto" />
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary-foreground bg-primary rounded-md px-1.5 py-0.5 font-display">OS</span>
          </div>

          <h1 className="text-2xl font-display font-bold text-white">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-7">Access your Dish Factory operations console.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11" placeholder="you@company.ae" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-amber-400 hover:underline" data-testid="forgot-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input id="password" data-testid="login-password" type={showPw ? "text" : "password"} value={password} autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)} required className="h-11 pr-11" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"} aria-pressed={showPw}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-white focus:outline-none focus:ring-2 focus:ring-ring">
                  {showPw ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full h-11 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold text-base">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Sign in
            </Button>
          </form>

          <p className="text-xs text-muted-foreground/70 mt-10 text-center">
            © {new Date().getFullYear()} DishFactory · Cloud Kitchen Operations Platform
          </p>
        </div>
      </div>
    </div>
  );
}
